import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import type { VolumePage } from '../../data/volumeFivePages';
type PageLightboxProps = {
  pages: VolumePage[];
  index: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
};
const EASE = [0.23, 1, 0.32, 1] as const;
export function PageLightbox({
  pages,
  index,
  onClose,
  onChange
}: PageLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const touchStart = useRef<{
    x: number;
    y: number;
  } | null>(null);
  const open = index !== null;
  const count = pages.length;
  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [open]);
  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && index < count - 1) onChange(index + 1);
      if (e.key === 'ArrowLeft' && index > 0) onChange(index - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, count, onChange, onClose]);
  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) {
      touchStart.current = null;
      return;
    }
    touchStart.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start || index === null) return;
    // Ignore swipes while the user is pinch-zoomed in.
    if (window.visualViewport && window.visualViewport.scale > 1.01) return;
    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0 && index < count - 1) onChange(index + 1);
    if (dx > 0 && index > 0) onChange(index - 1);
  };
  const page = index !== null ? pages[index] : null;
  return <AnimatePresence data-id="element-324" data-mp="1t0epi7ac62cb">
      {page && index !== null && <motion.div key="lightbox" role="dialog" aria-modal="true" aria-label={`Page preview: ${page.label}`} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} className="fixed inset-0 z-[100] flex items-center justify-center bg-v5-night/[0.92] font-v5sans" style={{
      touchAction: 'pinch-zoom'
    }} onClick={e => {
      if (e.target === e.currentTarget) onClose();
    }} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-id="element-325" data-mp="1sytted8dibcc">
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close preview" className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 ease-out hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-326" data-mp="ss6u35ul6hco">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-327" data-mp="a3jswyfj6c4d" />
          </button>

          <p className="absolute left-4 top-5 text-sm font-semibold text-white/80" aria-live="polite" data-id="element-328" data-mp="53xe4kk20ntg">
            {index + 1} / {count}
          </p>

          <AnimatePresence mode="wait" initial={false} data-id="element-329" data-mp="jtmkcu707i0l">
            <motion.img key={page.src} src={page.src} alt={`${page.label}: ${page.line}`} initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.18,
          ease: EASE
        }} className="pointer-events-auto aspect-[9/16] max-h-[calc(100dvh-88px)] max-w-[calc(100vw-24px)] rounded-xl object-contain" draggable={false} data-id="element-330" data-mp="f1bhpjkvpi4c" />
          </AnimatePresence>

          <p className="absolute bottom-3 left-1/2 w-[min(92vw,560px)] -translate-x-1/2 text-center text-[13px] leading-snug text-white/85" data-id="element-331" data-mp="2uqaa5w61xerv">
            <span className="font-bold text-white" data-id="element-332" data-mp="34hof4jgrv6se">{page.label}:</span> {page.line}
          </p>

          <button type="button" onClick={() => onChange(index - 1)} disabled={index === 0} aria-label="Previous page" className="absolute left-2 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-v5-plum shadow-lg transition-opacity duration-150 ease-out disabled:opacity-30 sm:flex sm:left-4" data-id="element-333" data-mp="39g95lr682c3n">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-334" data-mp="1o2mvygqrbocc" />
          </button>
          <button type="button" onClick={() => onChange(index + 1)} disabled={index === count - 1} aria-label="Next page" className="absolute right-2 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-v5-plum shadow-lg transition-opacity duration-150 ease-out disabled:opacity-30 sm:flex sm:right-4" data-id="element-335" data-mp="1gjdo4rxwfxpe">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-336" data-mp="1zirl7iqxdok4" />
          </button>
        </motion.div>}
    </AnimatePresence>;
}